import isEmpty from 'lodash.isempty'
import Vue from 'vue'

import { getFetchInstallmentSimulation } from '@http/endpoints'

/**
 * @typedef {import('@http/transformResponse/transformResponse').InstallmentSimulation} InstallmentSimulation
 */

export default {
  namespaced: true,

  /**
   * @typedef {Object} State
   * @property {import('@http/transformResponse/transformResponse').Price} basePrice
   * @property {number} installmentCount
   * @property {Object<string, InstallmentSimulation>} simulations
   *
   * @returns {State}
   */
  state: () => ({
    basePrice: {
      currency: '',
      amount: '0.00',
    },
    installmentCount: 0,
    simulations: {},
  }),

  mutations: {
    /**
     * @param {State} state
     * @param {import('@http/transformResponse/transformResponse').Price} basePrice
     */
    setBasePrice(state, basePrice) {
      state.basePrice = basePrice
    },

    /**
     * @param {State} state
     * @param {number} installmentCount
     */
    setInstallmentCount(state, installmentCount) {
      state.installmentCount = installmentCount
    },

    /**
     * @param {State} state
     * @param {Object} payload
     * @param {string} payload.index See `simulationIndex` getter
     * @param {InstallmentSimulation} payload.simulation
     */
    setSimulation(state, { index, simulation }) {
      Vue.set(state.simulations, index, simulation)
    },
  },

  getters: {
    /**
     * @param {State} state
     * @returns {boolean}
     */
    hasSimulation: (state) =>
      Number(state.basePrice.amount) > 0 && state.installmentCount > 0,

    /**
     * @param {State} state
     * @returns {number}
     */
    installmentCount: (state) => state.installmentCount,

    /**
     * @param {State} state
     * @returns {string}
     */
    simulationIndex: (state) => {
      const { basePrice, installmentCount } = state

      return [basePrice.currency, basePrice.amount, installmentCount].join('/')
    },

    /**
     * @param {State} state
     * @param {Object} getters
     * @returns {InstallmentSimulation | {}}
     */
    simulation: (state, getters) =>
      state.simulations[getters.simulationIndex] || {},
  },

  actions: {
    /**
     * @param {import('vuex').ActionContext<State, Object>} context
     */
    async updateSimulation({ state, getters, commit, dispatch, rootGetters }) {
      if (getters.hasSimulation && isEmpty(getters.simulation)) {
        commit('setSimulation', {
          index: getters.simulationIndex,
          simulation: {},
        })
        const { payload: simulation } = await dispatch(
          'http/request',
          {
            request: getFetchInstallmentSimulation(state.basePrice.currency),
            queryParams: {
              amount: state.basePrice.amount,
              nb_installments: state.installmentCount,
              country_code: rootGetters['config/country'],
            },
          },
          { root: true },
        )
        commit('setSimulation', { index: getters.simulationIndex, simulation })
      }
    },

    /**
     * @param {import('vuex').ActionContext<State, Object>} context
     * @param {import('@http/transformResponse/transformResponse').Price} basePrice
     */
    async setBasePrice({ commit, dispatch }, basePrice) {
      commit('setBasePrice', basePrice)
      await dispatch('updateSimulation')
    },

    /**
     * @param {import('vuex').ActionContext<State, Object>} context
     * @param {Object} payload
     * @param {import('@http/transformResponse/transformResponse').Price} payload.basePrice
     * @param {Object} payload.paymentMethod
     */
    async setPaymentMethod({ commit, dispatch }, { basePrice, paymentMethod }) {
      commit('setBasePrice', basePrice)
      commit('setInstallmentCount', paymentMethod.installmentCount || 0)
      await dispatch('updateSimulation')
    },
  },
}
